import { CheckCircle2, Minus, Plus } from "@/components/ui/icons";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";

import {
  type PullSheetEventPhase,
  pullSheetNoHoldWarning,
} from "@/components/pull-sheet/PullSheetBar";
import { Sheet, SheetList } from "@/components/ui/Sheet";
import type { PullSheet } from "@/hooks/usePullSheet";
import { formatCardIdentity } from "@/lib/catalog/cardIdentity";
import { formatMoney } from "@/lib/money/money";
import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

// Mirrors the backend's `CUSTOMER_LABEL_MAX` (#467), so the field reads as a short caller tag, not
// a notes box. The server truncates regardless.
const LABEL_MAX = 40;

type Props = {
  visible: boolean;
  onClose: () => void;
  sheet: PullSheet | null;
  /** Sets a line's absolute quantity from the review-cart stepper (#646); 0 removes the line. */
  onSetQuantity: (stockItemPublicId: string, quantity: number) => void;
  /**
   * Whether that line has a quantity write in flight (#646). The stepper drops taps while its
   * mutation settles, so it greys out, like the browse card's `PickControl`.
   */
  isPending?: (stockItemPublicId: string) => boolean;
  onPresent: (customerLabel?: string) => void;
  isPresenting: boolean;
  /**
   * Takes the list back out of the Vendor's line (TRO-674). Reachable only from the waiting view,
   * behind this modal's confirm step; the close X dismisses the view and withdraws nothing.
   */
  onWithdraw: () => void;
  isWithdrawing: boolean;
  /**
   * Whether the customer's withdraw succeeded. Read from the mutation, not `sheet`: success clears
   * the device-local id, so no sheet is left to carry a `withdrawn` status. Named for the action so
   * it is not mistaken for the sheet's lifecycle status.
   */
  justWithdrew: boolean;
  /**
   * The Vendor pulled this list and the sale is done. Needed alongside `sheet` because handling a
   * settle forgets the id, leaving no sheet to carry a `settled` status, as with `justWithdrew`.
   */
  justSettled: boolean;
  /**
   * The shop's public name, so the thank-you names the Vendor. The item detail payload carries no
   * shop name, so that surface falls back to a generic one.
   */
  shopName?: string | null;
  /**
   * The customer-facing message from the last failed remove or send (#360), or null. Shown inline,
   * because a floating toast would sit hidden behind the modal's backdrop.
   */
  error?: string | null;
  phase?: PullSheetEventPhase;
};

/**
 * The Pull Sheet review drawer: the editable pick list, then the waiting and done states (#342).
 *
 * While picking, the customer can change quantities and send the list (open -> presented), with an
 * optional short name or number so a busy Vendor can call them (#467, amends ADR-0014); left blank,
 * the sheet stays anonymous. Once `presented`, it shows a waiting confirmation with no QR (#343
 * covers that) and an armed "Leave the line" action (TRO-674, amends ADR-0014). A withdraw or a
 * settle ends in a confirmation and never falls back to the editable list.
 */
export function PullSheetReviewModal(
  {
    visible,
    onClose,
    sheet,
    onSetQuantity,
    isPending,
    onPresent,
    isPresenting,
    onWithdraw,
    isWithdrawing,
    justWithdrew,
    justSettled,
    shopName,
    error,
    phase = "live",
  }: Props,
) {
  const c = useThemeColors();
  const { height } = useWindowDimensions();
  const presented = sheet?.status === "presented";
  // The hook may already have forgotten the settled sheet, or this render may still hold it. Both
  // are a finished sale, so neither may fall through to the editable list and its Send button.
  const settled = justSettled || sheet?.status === "settled";
  // Only the editable list scrolls, so only it gets a height cap and a pinned footer. The other
  // views size to their content, where a percentage ceiling would clip the last element.
  const picking = !justWithdrew && !settled && !presented;
  const [label, setLabel] = useState("");
  // The waiting view's armed exit (TRO-674): the first tap arms, the second withdraws. Reset with
  // the label so a reopened modal never starts armed.
  const [leaving, setLeaving] = useState(false);

  const sheetPublicId = sheet?.public_id;
  useEffect(() => {
    setLabel("");
    setLeaving(false);
  }, [visible, sheetPublicId]);

  // Everything below the list goes in the sheet's `footer`, so the actions claim their height first
  // and the list shrinks into what is left instead of collapsing to nothing.
  const actions = (
    <>
      <View
        testID="pull-sheet-review-subtotal"
        className="flex-row items-center justify-between"
      >
        <Text className="text-base font-title text-foreground">
          Subtotal {formatMoney(sheet?.subtotal ?? "0")}
        </Text>
      </View>
      {
        /* Optional self-label (#467) so a busy Vendor can call the customer by it. Left blank,
           the sheet stays anonymous. */
      }
      <TextInput
        value={label}
        onChangeText={setLabel}
        maxLength={LABEL_MAX}
        placeholder="Your name or number (optional)"
        placeholderTextColor={c.subtle}
        returnKeyType="done"
        accessibilityLabel="Your name or number, optional — helps the Vendor find you"
        className="mt-4 rounded-xl border border-border bg-surface px-3 py-3 text-sm font-body text-foreground"
      />
      {/* Hands the list to the Vendor and puts the customer in line; it takes no payment. */}
      <Pressable
        testID="pull-sheet-checkout"
        onPress={() => onPresent(label.trim() || undefined)}
        disabled={!sheet || sheet.items.length === 0 || isPresenting}
        accessibilityRole="button"
        accessibilityLabel="Send your list to the Vendor"
        accessibilityState={{
          disabled: !sheet || sheet.items.length === 0 || isPresenting,
          busy: isPresenting,
        }}
        className="mt-3 items-center rounded-xl bg-primary py-3 disabled:opacity-40"
      >
        {isPresenting
          ? <ActivityIndicator />
          : (
            <Text className="text-base font-label text-primary-foreground">
              Send list
            </Text>
          )}
      </Pressable>
      {
        /* Persistent because sending a list neither takes payment nor reserves the card. */
      }
      <Text
        testID="pull-sheet-review-warning"
        className={cn(
          "mt-2 text-center text-xs font-body",
          phase === "upcoming" ? "font-semibold text-warning" : "text-muted-foreground",
        )}
      >
        You'll pay the Vendor in person. {pullSheetNoHoldWarning(phase)}
      </Text>
      {
        /* A failed remove or send (#360) shows inline, like the vendor-side ManageSheet's
          `text-loss` error line, so a failed tap is not silent. */
      }
      <ActionError message={error} className="mt-3" />
    </>
  );

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      testID="pull-sheet-review-modal"
      style={picking ? { maxHeight: height * 0.8 } : undefined}
      title="Your list"
      label="Your list"
      closeButton
      footer={picking ? actions : undefined}
    >
      {justWithdrew
        ? (
          <View
            key="withdrawn"
            testID="browse-withdrawn"
            className="items-center py-10"
            accessibilityLiveRegion="polite"
          >
            <Text className="mb-2 text-center text-base font-title text-foreground">
              You're out of the line
            </Text>
            <Text className="mb-6 text-center text-sm font-body text-muted-foreground">
              The Vendor won't pull this list. Start a new one whenever you like.
            </Text>
            <Pressable
              testID="pull-sheet-withdrawn-done"
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Done"
              className="items-center rounded-xl bg-primary px-8 py-3"
            >
              <Text className="text-base font-label text-primary-foreground">
                Done
              </Text>
            </Pressable>
          </View>
        )
        : settled
        ? (
          <View
            key="settled"
            testID="browse-settled"
            className="items-center py-8"
            accessibilityLiveRegion="polite"
          >
            <CheckCircle2 aria-hidden size={40} color={c.gain} />
            <Text className="mb-2 mt-4 text-center text-base font-title text-foreground">
              You're all set
            </Text>
            <Text className="mb-6 text-center text-sm font-body text-muted-foreground">
              {`${shopName || "The Vendor"} pulled your list. Thanks for shopping with them!`}
            </Text>
            {
              /* One action, because the device has already forgotten this sheet and tapping + while
                 browsing starts the next list. Nothing here reads as a receipt: Trovie took no
                 payment. */
            }
            <Pressable
              testID="pull-sheet-settled-done"
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Done"
              className="items-center rounded-xl bg-primary px-8 py-3"
            >
              <Text className="text-base font-label text-primary-foreground">
                Done
              </Text>
            </Pressable>
          </View>
        )
        : presented
        ? (
          <View
            key="waiting"
            testID="browse-queue-position"
            className="items-center py-10"
            accessibilityLiveRegion="polite"
          >
            {sheet?.queue_position != null && sheet?.queue_total != null
              ? (
                <>
                  <Text className="mb-1 text-center font-num-bold text-5xl text-primary">
                    #{sheet.queue_position}
                  </Text>
                  <Text className="mb-2 text-center text-base font-title text-foreground">
                    You're #{sheet.queue_position} of {sheet.queue_total} in line
                  </Text>
                  <Text className="text-center text-sm font-body text-muted-foreground">
                    The Vendor will call you when it's your turn. You'll pay them in person.
                  </Text>
                </>
              )
              : (
                <>
                  <Text className="mb-2 text-center text-base font-title text-foreground">
                    You're in line
                  </Text>
                  <Text className="text-center text-sm font-body text-muted-foreground">
                    They'll pull your list when they're ready for you. You'll pay them in person.
                  </Text>
                </>
              )}
            {
              /* The way out of the line (TRO-674), separate from the close X, which only dismisses
                 this view. Armed first, because a withdrawn place in line cannot be given back. */
            }
            {leaving
              ? (
                <View className="mt-8 w-full">
                  <Text className="mb-3 text-center text-sm font-body text-foreground">
                    Leave the line? The Vendor won't pull your list, and your picks go with it.
                  </Text>
                  <View className="flex-row gap-3">
                    <Pressable
                      testID="pull-sheet-withdraw-cancel"
                      onPress={() => setLeaving(false)}
                      accessibilityRole="button"
                      accessibilityLabel="Stay in line"
                      className="flex-1 items-center rounded-xl border border-border py-3"
                    >
                      <Text className="text-base font-label text-foreground">
                        Stay in line
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="pull-sheet-withdraw-confirm"
                      onPress={onWithdraw}
                      disabled={isWithdrawing}
                      accessibilityRole="button"
                      accessibilityLabel="Yes, leave the line"
                      accessibilityState={{ disabled: isWithdrawing, busy: isWithdrawing }}
                      className="flex-1 items-center rounded-xl border border-loss py-3 disabled:opacity-40"
                    >
                      {isWithdrawing
                        ? <ActivityIndicator />
                        : (
                          <Text className="text-base font-label text-loss">
                            Leave the line
                          </Text>
                        )}
                    </Pressable>
                  </View>
                </View>
              )
              : (
                <Pressable
                  testID="pull-sheet-withdraw"
                  onPress={() => setLeaving(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Leave the line"
                  className="mt-8 items-center px-4 py-2"
                >
                  <Text className="text-sm font-label text-muted-foreground underline">
                    Leave the line
                  </Text>
                </Pressable>
              )}
            {
              /* A refused withdraw (usually because the Vendor settled the list first) shows here:
                 the editable branch's error line is in a footer that `presented` never renders. */
            }
            <ActionError message={error} className="mt-4" />
          </View>
        )
        : (
          <SheetList
            key="picking"
            data={sheet?.items ?? []}
            keyExtractor={(item) => item.stock_item_public_id}
            ListEmptyComponent={
              <Text className="py-8 text-center text-sm font-body text-muted-foreground">
                Your list is empty.
              </Text>
            }
            renderItem={({ item }) => {
              // Set and collector number (#530) tell apart two same-name picks, so a customer
              // removes the right copy.
              const identity = formatCardIdentity(
                item.set_name,
                item.number,
                item.variant_label,
              );
              const condition = item.is_graded
                ? [item.grading_company, item.grade].filter(Boolean).join(" ") || "Graded"
                : item.condition;
              // Line total is the snapshotted unit price times the picked quantity (#646). "each"
              // shows only for more than one copy.
              const lineTotal = item.price != null
                ? formatMoney(String(Number(item.price) * item.quantity))
                : null;
              return (
                <View
                  testID="pull-sheet-review-line"
                  className="flex-row items-center justify-between border-b border-border py-3"
                >
                  <View className="flex-1 pr-3">
                    <Text className="text-sm font-title text-foreground">
                      {item.product_name}
                    </Text>
                    {identity || condition
                      ? (
                        <Text className="text-xs font-body text-muted-foreground">
                          {[identity, condition].filter(Boolean).join(" · ")}
                        </Text>
                      )
                      : null}
                    <Text className="text-sm text-muted-foreground">
                      {item.price != null ? formatMoney(item.price) : "Ask vendor"}
                      {item.quantity > 1 && item.price != null ? " each" : ""}
                    </Text>
                  </View>
                  <View className="items-end gap-1">
                    <CartStepper
                      quantity={item.quantity}
                      productName={item.product_name}
                      onChange={(next) => onSetQuantity(item.stock_item_public_id, next)}
                      disabled={isPending?.(item.stock_item_public_id) ?? false}
                    />
                    {lineTotal && item.quantity > 1
                      ? (
                        <Text className="font-num-bold text-sm text-foreground">
                          {lineTotal}
                        </Text>
                      )
                      : null}
                  </View>
                </View>
              );
            }}
          />
        )}
    </Sheet>
  );
}

/**
 * The modal's customer-facing failure line (#360), under the editable list's actions or the
 * waiting view's (TRO-674). Renders nothing without a message; `className` carries the spacing.
 */
function ActionError({ message, className }: { message?: string | null; className: string; }) {
  if (!message) return null;
  return (
    <Text
      className={cn("text-center text-sm font-body text-loss", className)}
      accessibilityLiveRegion="assertive"
    >
      {message}
    </Text>
  );
}

/**
 * The review-cart quantity stepper on one pick line (#646).
 *
 * Unlike the browse card's `PickControl`, a cart line always has at least one copy, so there is no
 * "add" state. The + has no client-side max because the line does not carry live availability; the
 * server clamps to stock and the returned sheet reflects it. The − at 1 sets 0, removing the line.
 */
function CartStepper({
  quantity,
  productName,
  onChange,
  disabled = false,
}: {
  quantity: number;
  productName: string;
  onChange: (next: number) => void;
  disabled?: boolean;
}) {
  const c = useThemeColors();
  return (
    <View
      className={cn(
        "flex-row items-center rounded-full border-2 border-primary bg-surface",
        disabled && "opacity-50",
      )}
    >
      <Pressable
        onPress={() => onChange(quantity - 1)}
        disabled={disabled}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={quantity <= 1 ? `Remove ${productName}` : `One fewer ${productName}`}
        accessibilityState={{ disabled }}
        className={cn("h-12 w-12 items-center justify-center", disabled && "opacity-40")}
      >
        <Minus size={15} color={c.primary} strokeWidth={3} />
      </Pressable>
      <Text
        className="min-w-[22px] text-center font-num-bold text-sm text-foreground"
        accessibilityLabel={`Quantity ${quantity}`}
      >
        {quantity}
      </Text>
      <Pressable
        onPress={() => onChange(quantity + 1)}
        disabled={disabled}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={`One more ${productName}`}
        accessibilityState={{ disabled }}
        className={cn("h-12 w-12 items-center justify-center", disabled && "opacity-40")}
      >
        <Plus size={15} color={c.primary} strokeWidth={3} />
      </Pressable>
    </View>
  );
}
